import { useEffect, useRef, useState } from 'react';
import { Client, IMessage } from '@stomp/stompjs';

interface UseWebSocketOptions {
  topic: string;
  onMessage: (message: any) => void;
  enabled?: boolean;
  /**
   * STOMP CONNECT headers, e.g. `{ Authorization: 'Bearer …' }` for a topic the
   * server guards (the mail monitor). Read when the client (re)connects.
   */
  connectHeaders?: Record<string, string>;
}

const LOCAL_HOST = /^(localhost|127\.0\.0\.1|\[::1\])$/i;

/**
 * The address of the STOMP endpoint, resolved at run time rather than baked in.
 *
 * Vite inlines import.meta.env at BUILD time, so a build made while .env still
 * carried the developer's own http://localhost:8082 ships that string to every
 * visitor - each browser then tries to open a socket to its own machine, which
 * is refused, and the connection indicator stays red on the live site while the
 * server logs show nothing at all. The localhost guard below catches exactly
 * that mis-build: when the page itself is served from a real host, a localhost
 * API base cannot be right, so the page's own origin is used instead.
 */
export function resolveWsUrl(
  configured: string | undefined = import.meta.env.VITE_API_BASE_URL,
  location: { origin: string; hostname: string } = window.location
): string {
  const pageIsLocal = LOCAL_HOST.test(location.hostname);
  const raw = (configured ?? '').trim();

  // Relative bases ("/", "/api-host/") resolve against the page; absolute ones
  // keep their own host.
  let base: URL;
  try {
    base = new URL(raw === '' ? location.origin : raw, location.origin);
  } catch {
    base = new URL(location.origin);
  }

  if (LOCAL_HOST.test(base.hostname) && !pageIsLocal) {
    console.warn(
      `[STOMP] VITE_API_BASE_URL is "${raw}" but this page is served from ` +
      `${location.origin}. This bundle was built with the development .env; ` +
      `falling back to the page origin. Rebuild with .env.production set.`
    );
    base = new URL(location.origin);
  }

  const url = new URL('/api/ws', base);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return url.toString();
}

/** Hides a bearer token in a logged STOMP frame. */
export function maskAuthorization(text: string): string {
  return text.replace(/(authorization:\s*Bearer\s+)\S+/gi, '$1****');
}

export function useWebSocket({ topic, onMessage, enabled = true, connectHeaders }: UseWebSocketOptions) {
  const [isConnected, setIsConnected] = useState(false);
  const clientRef = useRef<Client | null>(null);

  const wsUrl = resolveWsUrl();

  const onMessageRef = useRef(onMessage);
  const connectHeadersRef = useRef(connectHeaders);

  // Keep the ref updated with the latest onMessage callback
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  // A refreshed token is picked up on the next reconnect without tearing the socket down.
  useEffect(() => {
    connectHeadersRef.current = connectHeaders;
    if (clientRef.current && connectHeaders) {
      clientRef.current.connectHeaders = connectHeaders;
    }
  }, [connectHeaders]);

  useEffect(() => {
    if (!enabled) return;

    const client = new Client({
      brokerURL: wsUrl,
      ...(connectHeadersRef.current ? { connectHeaders: connectHeadersRef.current } : {}),
      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
      // A reverse proxy that completes the HTTP upgrade but never relays the
      // frames leaves the socket open and silent: the handshake succeeds, the
      // CONNECTED frame never arrives, and without this the client waits on it
      // forever instead of tearing down and retrying.
      connectionTimeout: 10000,
      // Enable extreme debug logging to see exactly why the connection fails in Live (IIS)
      // Frames are printed with their headers; the token in a CONNECT header is masked.
      debug: (str) => {
        console.log(`[STOMP DEBUG] ${maskAuthorization(str)}`);
      },
      onConnect: () => {
        setIsConnected(true);
        console.log(`[STOMP] Successfully connected to WebSocket: ${wsUrl}`);

        // Subscribe to the topic
        client.subscribe(topic, (message: IMessage) => {
          try {
            const body = message.body ? JSON.parse(message.body) : message.body;
            onMessageRef.current(body);
          } catch (e) {
            onMessageRef.current(message.body);
          }
        });
      },
      onDisconnect: () => {
        setIsConnected(false);
        console.log('[STOMP] Disconnected from WebSocket');
      },
      onStompError: (frame) => {
        console.error('[STOMP] Broker reported error: ' + frame.headers['message']);
        console.error('[STOMP] Additional details: ' + frame.body);
      },
      onWebSocketError: (event) => {
        console.error('[STOMP] WebSocket Error event:', event);
      },
      onWebSocketClose: (event) => {
        // The socket is gone, so the indicator must go red again; without this
        // a dropped connection still reads as connected until the retry lands.
        setIsConnected(false);
        console.warn(`[STOMP] WebSocket Closed. Code: ${event.code}, Reason: "${event.reason}", Clean: ${event.wasClean}`);
      }
    });

    client.activate();
    clientRef.current = client;

    return () => {
      if (clientRef.current) {
        clientRef.current.deactivate();
      }
    };
  }, [topic, enabled, wsUrl]);

  return { isConnected };
}
